"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { GoogleAdsPixel } from "@/components/analytics/GoogleAdsPixel";
import { RedditPixel } from "@/components/analytics/RedditPixel";
import { XPixel } from "@/components/analytics/XPixel";
import { PostHogBoot } from "@/components/layout/DeferredProviders";

const TRACKING_BOOT_DELAY_MS = 3500;

interface GrowthTrackingProps {
  pathname?: string;
}

export function GrowthTracking({ pathname }: GrowthTrackingProps = {}) {
  const resolvedPathname = usePathname() ?? pathname;
  const [shouldLoadTracking, setShouldLoadTracking] = useState(false);
  const [loadXPixelNow, setLoadXPixelNow] = useState(false);

  useEffect(() => {
    const landedFromXAd = Boolean(new URLSearchParams(window.location.search).get("twclid"));
    const onSignupCompletion = window.location.pathname.startsWith("/auth/complete-signup");
    setLoadXPixelNow(landedFromXAd || onSignupCompletion);
  }, []);

  useEffect(() => {
    const startTimer = () => {
      const timer = window.setTimeout(() => {
        setShouldLoadTracking(true);
      }, TRACKING_BOOT_DELAY_MS);

      return () => window.clearTimeout(timer);
    };

    if (document.readyState === "complete") {
      return startTimer();
    }

    let cleanupTimer: (() => void) | undefined;
    const handleLoad = () => {
      cleanupTimer = startTimer();
    };

    window.addEventListener("load", handleLoad, { once: true });

    return () => {
      window.removeEventListener("load", handleLoad);
      cleanupTimer?.();
    };
  }, []);

  return (
    <>
      <PostHogBoot pathname={resolvedPathname} />
      {loadXPixelNow ? <XPixel strategy="afterInteractive" /> : null}
      {shouldLoadTracking ? (
        <>
          {loadXPixelNow ? null : <XPixel />}
          <RedditPixel />
          <GoogleAdsPixel />
        </>
      ) : null}
    </>
  );
}
